import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Table, Form, Button, Alert, Card } from 'react-bootstrap';
import moment from 'moment';
import ComebackArrow from '../../components/ComebackArrow/ComebackArrow';
import { alertService } from '../../services/AlertService';
import EsamiUtentiService from '../../services/EsamiUtentiService/EsamiUtentiService';
import useMediaQuery from '../../hooks/useMediaQuery';
import IconWithTooltip from '../../components/IconWithTooltip/IconWithTooltip';
import CustomButton from '../../components/CustomButton/CustomButton';

const GestioneRisultatiEsamePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { examData, classeData } = location.state || {};
  const [corsistiPrenotati, setCorsistiPrenotati] = useState([]);
  const [statinoFiles, setStatinoFiles] = useState({});
  const [existingStatini, setExistingStatini] = useState({});
  const isWideScreen = useMediaQuery('(min-width: 1280px)');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!examData) {
      navigate('/configura-esami');
      return;
    }
    setCorsistiPrenotati(examData.utenti.filter(u => u.prenotato));
    const existingFiles = {};
    examData.utenti.forEach(u => {
      if (u.file_statino) {
        existingFiles[u.id_calendario_esame_utente] = u.file_statino.split('/').pop();
      }
    });
    setExistingStatini(existingFiles);
  }, [examData, navigate]);

  const handleInputChange = (idUtente, field, value) => {
    setCorsistiPrenotati(prev => prev.map(u => 
      u.id_utente === idUtente ? { ...u, [field]: value } : u
    ));
  };

  const handleStatinoChange = (idCalendarioEsameUtente, file) => {
    setStatinoFiles(prev => ({
      ...prev,
      [idCalendarioEsameUtente]: file
    }));
    setExistingStatini(prev => {
      const newState = { ...prev };
      delete newState[idCalendarioEsameUtente];
      return newState;
    });
  };

  const handleRemoveStatino = (idCalendarioEsameUtente) => {
    setStatinoFiles(prev => {
      const newState = { ...prev };
      delete newState[idCalendarioEsameUtente];
      return newState;
    });
    setExistingStatini(prev => {
      const newState = { ...prev };
      delete newState[idCalendarioEsameUtente];
      return newState;
    });
  };

  const handleRemoveExistingStatino = (idCalendarioEsameUtente) => {
    setExistingStatini(prev => {
      const newState = { ...prev };
      delete newState[idCalendarioEsameUtente];
      return newState;
    });
  };

  const handlePresenzaChange = (idUtente, value) => {
    setCorsistiPrenotati(prev => prev.map(u => 
      u.id_utente === idUtente ? { ...u, presenza: value } : u
    ));
  };

  const renderPresenzaSwitch = (corsista) => (
    <Form.Check 
      type="switch"
      id={`presenza-${corsista.id_utente}`}
      label={corsista.presenza === 'presente' ? 'Presente' : 'Assente'}
      checked={corsista.presenza === 'presente'}
      onChange={(e) => handlePresenzaChange(corsista.id_utente, e.target.checked ? 'presente' : 'assente')}
    />
  );

  const handleSaveResults = async () => {
    setIsLoading(true);
    try {
      const esitiToSend = corsistiPrenotati.map(corsista => ({
        ...corsista,
        presenza: corsista.presenza === 'non_registrato' ? 'assente' : corsista.presenza
      }));
      await EsamiUtentiService.updateEsameUtentiResults({esiti: esitiToSend});
      for (const corsista of corsistiPrenotati) {
        if (corsista.presenza === 'presente') {
          const idCalendarioEsameUtente = corsista.id_calendario_esame_utente;
          if (statinoFiles[idCalendarioEsameUtente]) {
            const formData = new FormData();
            formData.append('statino', statinoFiles[idCalendarioEsameUtente]);
            formData.append('id_calendario_esame_utente', idCalendarioEsameUtente);
            await EsamiUtentiService.uploadStatino(formData);
          } else if (!existingStatini[idCalendarioEsameUtente] && corsista.file_statino) {
            await EsamiUtentiService.removeStatino({ id_calendario_esame_utente: idCalendarioEsameUtente });
          }
        }
      }
      alertService.success('Risultati e statini salvati con successo', { autoClose: true });
      navigate('/configura-esami', { state: { classeData } });
    } catch (error) {
      alertService.error('Errore nel salvataggio dei risultati e degli statini', { autoClose: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadStatino = async (idCalendarioEsameUtente, filename) => {
    try {
      await EsamiUtentiService.downloadStatino({ id_calendario_esame_utente: idCalendarioEsameUtente, filename: filename });
    } catch (error) {
      alertService.error('Errore nel download dello statino');
    }
  };

  const renderCorsistaCard = (corsista) => (
    <Card className="mb-3">
      <Card.Body>
        <Card.Title>{corsista.nome} {corsista.cognome}</Card.Title>
        <Form>
          <Form.Group className="mb-3">
            {renderPresenzaSwitch(corsista)}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Esito</Form.Label>
            <Form.Select
              value={corsista.esito_esame}
              onChange={(e) => handleInputChange(corsista.id_utente, 'esito_esame', e.target.value)}
              disabled={corsista.presenza !== 'presente'}
            >
              <option value="non_svolto">Non svolto</option>
              <option value="promosso">Promosso</option>
              <option value="bocciato">Bocciato</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Voto</Form.Label>
            <Form.Control
              type="text"
              value={corsista.voto || ''}
              onChange={(e) => handleInputChange(corsista.id_utente, 'voto', e.target.value)}
              disabled={corsista.presenza !== 'presente' || corsista.esito_esame === 'non_svolto'}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={corsista.note || ''}
              onChange={(e) => handleInputChange(corsista.id_utente, 'note', e.target.value)}
              disabled={corsista.presenza !== 'presente'}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Statino</Form.Label>
            {renderStatinoActions(corsista)}
          </Form.Group>
        </Form>
      </Card.Body>
    </Card>
  );

  const renderStatinoActions = (corsista) => {
    if (corsista.presenza !== 'presente') {
      return <span>{" "}Non richiesto</span>;
    }

    const fileName = corsista.nome_file_statino || (statinoFiles[corsista.id_calendario_esame_utente] && statinoFiles[corsista.id_calendario_esame_utente].name);
    if (existingStatini[corsista.id_calendario_esame_utente]) {
      return (
        <div className="d-flex align-items-center">
          <IconWithTooltip
            icon="bi-download"
            text="Scarica Statino"
            onClick={() => handleDownloadStatino(corsista.id_calendario_esame_utente, corsista.nome_file_statino)}
            variant='secondary'
            className="p-0 me-2"
          />
          <span>{fileName || existingStatini[corsista.id_calendario_esame_utente]}</span>
          <IconWithTooltip
            icon="bi-trash"
            text="Rimuovi Statino"
            onClick={() => handleRemoveExistingStatino(corsista.id_calendario_esame_utente)}
            variant="danger"
            className="ms-2"
          />
        </div>
      );
    } else if (statinoFiles[corsista.id_calendario_esame_utente]) {
      return (
        <div className="d-flex align-items-center">
          <i className="bi bi-file-earmark-pdf text-success me-2"></i>
          <span>{statinoFiles[corsista.id_calendario_esame_utente].name}</span>
          <IconWithTooltip
            icon="bi-x-lg"
            text="Rimuovi Statino"
            onClick={() => handleRemoveStatino(corsista.id_calendario_esame_utente)}
            variant="secondary"
            className="ms-2"
          />
        </div>
      );
    } else {
      return (
        <div className="text-center">
          <label htmlFor={`statino-upload-${corsista.id_calendario_esame_utente}`} className="btn btn-primary btn-sm">
             Carica Statino
          </label>
          <input
            id={`statino-upload-${corsista.id_calendario_esame_utente}`}
            type="file"
            accept=".pdf"
            onChange={(e) => handleStatinoChange(corsista.id_calendario_esame_utente, e.target.files[0])}
            style={{ display: 'none' }}
          />
        </div>
      );
    }
  };

  const renderContent = () => {
    if (corsistiPrenotati.length === 0) {
      return (
        <Alert variant="secondary" className="mt-4">
          <Alert.Heading>Nessun utente prenotato</Alert.Heading>
          <p>
            Non ci sono utenti prenotati per questo esame. 
            Gli studenti possono prenotarsi all'esame attraverso la loro area personale.
          </p>
          <hr />
          <p className="mb-0">
            Controlla più tardi per eventuali aggiornamenti o contatta il supporto se ritieni che ci sia un errore.
          </p>
        </Alert>
      );
    }

    return (
      <>
        <Alert variant="secondary" className='mt-3'>
          Inserisci i risultati dell'esame per ciascun corsista. Assicurati di caricare lo statino per ogni studente presente.
        </Alert>

        <div className='mt-4'>
          {isWideScreen ? (
            <Table responsive striped bordered hover>
              <thead>
                <tr>
                  <th>Corsista</th>
                  <th>Presenza</th>
                  <th>Esito</th>
                  <th>Voto</th>
                  <th>Note</th>
                  <th>Statino</th>
                </tr>
              </thead>
              <tbody>
                {corsistiPrenotati.map((corsista) => (
                  <tr key={corsista.id_utente}>
                    <td>{corsista.nome} {corsista.cognome}</td>
                    <td>
                      {renderPresenzaSwitch(corsista)}
                    </td>
                    <td>
                      <Form.Select
                        value={corsista.esito_esame}
                        onChange={(e) => handleInputChange(corsista.id_utente, 'esito_esame', e.target.value)}
                        disabled={corsista.presenza !== 'presente'}
                      >
                        <option value="non_svolto">Non svolto</option>
                        <option value="promosso">Promosso</option>
                        <option value="bocciato">Bocciato</option>
                      </Form.Select>
                    </td>
                    <td style={{ width: '70px' }}>
                      <Form.Control
                        type="number"
                        value={corsista.voto || ''}
                        onInput={(e) => e.target.value = e.target.value.replace(/-/g, '')}
                        onChange={(e) => handleInputChange(corsista.id_utente, 'voto', e.target.value)}
                        disabled={corsista.presenza !== 'presente' || corsista.esito_esame === 'non_svolto'}
                        style={{ width: '100%' }}
                      />
                    </td>
                    <td>
                      <Form.Control
                        as="textarea"
                        rows={1}
                        value={corsista.note || ''}
                        onChange={(e) => handleInputChange(corsista.id_utente, 'note', e.target.value)}
                        disabled={corsista.presenza !== 'presente'}
                      />
                    </td>
                    <td className='text-center'>
                      {renderStatinoActions(corsista)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            corsistiPrenotati.map((corsista) => renderCorsistaCard(corsista))
          )}
        </div>

        <div className='mt-4 text-end'>
          <CustomButton 
            onClick={handleSaveResults}
            isLoading={isLoading}
            label="Salva"
            size="lg"
          />
        </div>
      </>
    );
  };

  return (
    <div className={`card custom-card w-100 shadow ${isWideScreen ? 'p-5' : 'px-1 py-3'}`} style={{minHeight: '700px'}}>
      <ComebackArrow path="/configura-esami" state={{ classeData }} />
      <div className='container'>
        <div className='d-flex justify-content-between custom-card-header'>
          <div className='d-flex gap-3 custom-card-title my-2'>
            <div className={`custom-icon-container`}>
              {classeData?.corso?.immagine_corso ? 
                <img src={classeData?.corso?.immagine_corso} className='custom-icon' alt='immagine corso'/> : 
                <i className={`bi custom-icon bi-calendar-event fs-2`}></i>}
            </div>
            <div className='align-self-center'>
              <span className='fs-4 d-block'>
                <i><b>{classeData.corso.denominazione} - </b></i>
                Esame del {moment(examData.data).format('DD/MM/YYYY')}, 
                Orario: {examData.ora_inizio.slice(0, 5)} - {examData.ora_fine.slice(0, 5)}
              </span>
            </div>
          </div>
        </div>

        {renderContent()}
      </div>
    </div>
  );
};

export default GestioneRisultatiEsamePage;
